import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Contrast } from '@entur/layout';
import { UserIcon } from '@entur/icons';
import { IconButton } from '@entur/button';
import { Grid } from '@entur/layout/beta';

import { fullWidthColSpan } from '~/utils/contentWrapperColSpan';
import { Media } from '~/templates/MainWrapper';
import { LanguagePicker } from '../header/LanguagePicker';
import { MobileMenu } from './MobileMenu';
import Menu from './Menu';

import './Navigation.scss';
import './BurgerMenu.scss';

type NavigationProps = {
  isContrast?: boolean;
};

function useOutsideAlerter(
  ref: React.RefObject<HTMLElement>,
  excludeRef: React.RefObject<HTMLElement>,
  onOutside: () => void
) {
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        !excludeRef.current?.contains(event.target as Node)
      ) {
        onOutside();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, excludeRef, onOutside]);
}

const Navigation: React.FC<NavigationProps> = ({ isContrast }) => {
  const { formatMessage: f } = useIntl();
  const [open, setOpen] = React.useState(false);
  const [anim, setAnim] = React.useState(false);

  const isContrastMode = isContrast || anim;
  const Element = isContrastMode ? Contrast : 'div';

  const data = useStaticQuery(graphql`
    query {
      logoNegative: file(relativePath: { eq: "LogoNegative.svg" }) {
        publicURL
      }
      logoPositive: file(relativePath: { eq: "LogoPositive.svg" }) {
        publicURL
      }
    }
  `);

  React.useEffect(() => {
    if (open) {
      setAnim(true);
    }
  }, [open]);

  const handleMenuClosed = () => {
    setAnim(false);
  };

  const menuButtonClassNames = classNames('burger-menu-button', {
    'burger-menu-button--is-toggled': open,
  });

  const getIconClassNames = (index: number): string =>
    classNames(
      'burger-menu-button__icon',
      `burger-menu-button__icon__${index}`,
      {
        'burger-menu-button__icon--contrast': !isContrast && !open,
      }
    );

  const backdropRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);

  useOutsideAlerter(backdropRef, headerRef, () => setOpen(false));

  const logoSrc = isContrastMode
    ? data.logoNegative.publicURL
    : data.logoPositive.publicURL;

  const burgerIcons = (
    <span aria-hidden="true" className="burger-menu-button__icons">
      <span className={getIconClassNames(1)} />
      <span className={getIconClassNames(2)} />
      <span className={getIconClassNames(3)} />
    </span>
  );

  return (
    <Grid.Item
      as="header"
      colSpan={fullWidthColSpan}
      className={classNames('header-grid', { 'menu-is-open': open })}
      ref={headerRef}
    >
      <Media
        greaterThanOrEqual="desktop"
        className={classNames('omtur-desktop-navigation', {
          'eds-contrast': isContrastMode,
        })}
      >
        <Element className="omtur-desktop-navigation__inner">
          <a href="https://entur.no/" className="omtur-navigation__logo-link">
            <img
              height="32px"
              alt={f({ id: 'navigation.logoAlt' })}
              src={logoSrc}
            />
          </a>
          <div className="omtur-desktop-navigation__actions">
            <div className="omtur-desktop-navigation__language-picker">
              <LanguagePicker
                iconClassName="omtur-desktop-navigation__language-picker__icon"
                labelClassName="omtur-desktop-navigation__language-picker__label"
              />
            </div>
            <IconButton
              className="omtur-navigation__min-side-button"
              as="a"
              href="https://www.entur.no/min-side"
            >
              <span className="omtur-navigation__button-text">
                <FormattedMessage id="navigation.minSide" />
              </span>
              <UserIcon size="20" />
            </IconButton>
            <IconButton
              className={classNames(
                menuButtonClassNames,
                'omtur-navigation__menu-button'
              )}
              onClick={() => setOpen(!open)}
              aria-label={f({ id: 'navigation.menu' })}
            >
              <span className="omtur-navigation__button-text">
                <FormattedMessage id="navigation.menu" />
              </span>
              {burgerIcons}
            </IconButton>
          </div>
        </Element>
        <Menu
          open={open}
          closeMenu={() => setOpen(false)}
          isDoneAnimate={handleMenuClosed}
          ref={backdropRef}
        />
      </Media>
      <Media at="mobile" className="omtur-mobile-navigation">
        <Element className="omtur-mobile-navigation__element">
          <div className="omtur-mobile-navigation__top-bar">
            <a href="https://entur.no/" className="omtur-navigation__logo-link">
              <img
                height="25px"
                alt={f({ id: 'navigation.logoAlt' })}
                src={logoSrc}
              />
            </a>
            <div className="omtur-mobile-navigation__language-picker">
              <LanguagePicker
                iconClassName="omtur-mobile-navigation__language-picker__icon"
                labelClassName="omtur-mobile-navigation__language-picker__label"
              />
            </div>
          </div>
          <div className="omtur-mobile-navigation__actions">
            <IconButton
              className="omtur-navigation__min-side-button"
              as="a"
              href="https://www.entur.no/min-side"
            >
              <span className="omtur-navigation__button-text omtur-navigation__button-text--nowrap">
                <FormattedMessage id="navigation.minSide" />
              </span>
              <UserIcon size="20" />
            </IconButton>
            <IconButton
              className="omtur-navigation__menu-button"
              onClick={() => setOpen(!open)}
              aria-label={f({ id: 'navigation.menu' })}
            >
              <span className="omtur-navigation__button-text">
                <FormattedMessage id="navigation.menu" />
              </span>
              <div aria-hidden="true" className="burger-menu-button__icons">
                <span className={getIconClassNames(1)} />
                <span className={getIconClassNames(2)} />
                <span className={getIconClassNames(3)} />
              </div>
            </IconButton>
          </div>
        </Element>
        <MobileMenu
          open={open}
          isDoneAnimate={handleMenuClosed}
          closeMenu={() => setOpen(false)}
        />
      </Media>
    </Grid.Item>
  );
};

export default Navigation;
